import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import { parseEntityRef } from '@backstage/catalog-model';
import useAsync from 'react-use/lib/useAsync';

const SubgroupPickerFieldSchema = makeFieldSchema({
  output: z => z.string(),
  uiOptions: z =>
    z.object({
      team: z
        .string()
        .optional()
        .describe(
          'Entity ref of the team group whose subgroups (roles) will be listed, e.g. "group:devops/devops". Falls back to formData.team if omitted.',
        ),
    }),
});

export const SubgroupPickerSchema = SubgroupPickerFieldSchema;

type SubgroupPickerProps = typeof SubgroupPickerFieldSchema.TProps;

export const SubgroupPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  formContext,
  uiSchema,
  idSchema,
}: SubgroupPickerProps) => {
  const catalogApi = useApi(catalogApiRef);

  // Extract namespace from the team field
  const subgroupLead =
    formContext.formData?.team || uiSchema['ui:options']?.team;
  const namespace =
    typeof subgroupLead === 'string' && subgroupLead
      ? parseEntityRef(subgroupLead).namespace
      : undefined;

  // useAsync cleanly handles loading state, error catching, and component unmounts
  const { value: subgroups = [], loading } = useAsync(async () => {
    if (!namespace) return [];

    const { items } = await catalogApi.getEntities({
      filter: { kind: 'Group', 'metadata.namespace': namespace },
    });
    return items;
  }, [catalogApi, namespace]);

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
      fullWidth
    >
      <InputLabel htmlFor={idSchema?.$id}>Subgroup</InputLabel>
      <Select
        id={idSchema?.$id}
        value={formData || ''}
        disabled={loading || !namespace}
        onChange={e => onChange(e.target.value as string)}
      >
        {subgroups.map(group => (
          <MenuItem key={group.metadata.name} value={group.metadata.name}>
            {group.metadata.title || group.metadata.name}
          </MenuItem>
        ))}
        {subgroups.length === 0 && !loading && (
          <MenuItem value="" disabled>
            {namespace ? 'No subgroups found' : 'Please select a group first'}
          </MenuItem>
        )}
      </Select>
      <FormHelperText>
        {rawErrors?.length > 0
          ? rawErrors[0]
          : 'Select a subgroup within the specified group'}
      </FormHelperText>
    </FormControl>
  );
};

export const SubgroupPickerExamples = [
  {
    description:
      'Dynamic team — reads the team entity ref from a previous form field and lists all subgroups (roles) within that namespace.',
    example: {
      'ui:field': 'SubgroupPicker',
      'ui:options': {},
    },
  },
  {
    description:
      'Static team — hardcodes the team entity ref so the role dropdown is always scoped to that team.',
    example: {
      'ui:field': 'SubgroupPicker',
      'ui:options': {
        team: 'group:devops/devops',
      },
    },
  },
];
